import { useEffect, useMemo, useState } from 'react'
import { Autocomplete, Box } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { projectService } from '@/services/projectService'
import { DeskInput, Field, Group, StepHeading } from './desk'
import { marginOfErrorPp } from './estimate'
import type { DraftPatch, StudyDraft } from './types'

type StepProps = {
  draft: StudyDraft
  onChange: (patch: DraftPatch) => void
}

type ClientOption = { name: string; studies: number }

/** Clients the researcher has already used, most frequent first. */
function useKnownClients(): ClientOption[] {
  const [clients, setClients] = useState<ClientOption[]>([])
  useEffect(() => {
    let cancelled = false
    projectService
      .getProjects()
      .then((projects) => {
        if (cancelled) return
        const counts = new Map<string, number>()
        for (const p of projects) {
          const name = (p.client ?? '').trim()
          if (name) counts.set(name, (counts.get(name) ?? 0) + 1)
        }
        setClients([...counts].map(([name, studies]) => ({ name, studies })).sort((a, b) => b.studies - a.studies))
      })
      .catch(() => !cancelled && setClients([]))
    return () => {
      cancelled = true
    }
  }, [])
  return clients
}

/*
 * Step 1 asks only for what something downstream reads: the name (list,
 * reports, the respondent's invitation), the client (filtering the list), the
 * target (progress on the dashboard) and a note for the team. The old "goal"
 * chips and "audience" field were dropped on 2026-10-08 — they never reached
 * the Project and step 3 never used them, despite the hint saying it would.
 * `draft.goals` / `draft.audience` stay in the type so old drafts still load.
 */
export default function WizardStepBasics({ draft, onChange }: StepProps) {
  const c = useTheme().affexy.colors
  const { t } = useTranslation('dashboard')
  const clients = useKnownClients()
  const target = Math.round(Number(draft.targetRespondents))
  const moe = marginOfErrorPp(target)
  const nameMissing = draft.name.trim().length === 0

  const clientOptions = useMemo(() => clients.map((cl) => cl.name), [clients])
  const studiesOf = (name: string) => clients.find((cl) => cl.name === name)?.studies ?? 0

  return (
    <>
      <StepHeading title={t('wizard.desk.basics.title')} lead={t('wizard.desk.basics.lead')} />

      <Group title={t('wizard.desk.basics.studyTitle')} description={t('wizard.desk.basics.studyDesc')}>
        <Field label={t('wizard.basics.nameLabel')}>
          <DeskInput
            autoFocus={nameMissing}
            value={draft.name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder={t('wizard.desk.basics.namePlaceholder')}
            slotProps={{ htmlInput: { 'aria-label': t('wizard.basics.nameLabel'), maxLength: 200 } }}
          />
        </Field>
        <Field label={t('wizard.basics.clientLabel')} optional={t('wizard.desk.optional')} help={t('wizard.desk.basics.clientHelp')}>
          <Autocomplete
            freeSolo
            options={clientOptions}
            inputValue={draft.client}
            onInputChange={(_, value) => onChange({ client: value })}
            renderOption={({ key, ...props }, option) => (
              <Box component="li" key={key} {...props} sx={{ display: 'flex', gap: 1, fontSize: 14 }}>
                {option}
                <Box component="span" sx={{ ml: 'auto', fontSize: 12, color: c.ink48 }}>
                  {t('wizard.desk.basics.clientStudies', { count: studiesOf(option) })}
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <DeskInput
                {...params}
                placeholder={t('wizard.desk.basics.clientPlaceholder')}
                slotProps={{ input: params.InputProps, htmlInput: params.inputProps }}
                sx={{ '& .MuiOutlinedInput-root': { py: '0 !important', pl: '0 !important' }, '& .MuiAutocomplete-input': { pl: '14px !important' } }}
              />
            )}
          />
        </Field>
      </Group>

      <Group title={t('wizard.desk.basics.recruitTitle')} description={t('wizard.desk.basics.recruitDesc')}>
        <Field
          label={t('wizard.desk.basics.targetLabel')}
          // One line under the field, so a three-digit target never pushes the
          // margin of error into a second column that wraps (Z-124).
          help={
            moe != null ? (
              <>
                {t('wizard.desk.basics.moe')}{' '}
                <Box component="b" sx={{ color: c.ink, fontVariantNumeric: 'tabular-nums' }}>
                  ±{Math.round(moe)} pp
                </Box>
                <Box component="span" sx={{ color: c.ink48 }}>
                  {' · '}
                  {t('wizard.desk.basics.moeCompare', {
                    small: Math.round(marginOfErrorPp(30) ?? 0),
                    big: Math.round(marginOfErrorPp(100) ?? 0),
                  })}
                </Box>
              </>
            ) : undefined
          }
        >
          <DeskInput
            type="number"
            value={draft.targetRespondents}
            onChange={(e) => onChange({ targetRespondents: e.target.value })}
            suffix={t('wizard.basics.targetSuffix')}
            slotProps={{ htmlInput: { min: 1, max: 100000, 'aria-label': t('wizard.desk.basics.targetLabel') } }}
            sx={{ maxWidth: 220 }}
          />
        </Field>
      </Group>

      <Group title={t('wizard.desk.basics.noteTitle')} description={t('wizard.desk.basics.noteDesc')}>
        <DeskInput
          multiline
          minRows={3}
          value={draft.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder={t('wizard.desk.basics.notePlaceholder')}
          slotProps={{ htmlInput: { 'aria-label': t('wizard.desk.basics.noteTitle') } }}
        />
      </Group>
    </>
  )
}
